import React from 'react';
import { Card } from 'semantic-ui-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { useBloodTestData, useBloodTestInfo } from '../context/BloodTestDataContext';

export default function TestChart({ testName }) {
  const { selectedPatientId, getTestTimeSeries } = useBloodTestData();
  const { getTestDefinition } = useBloodTestInfo();

  const timeSeries = getTestTimeSeries(selectedPatientId, testName);
  const definition = getTestDefinition(testName);

  if (timeSeries.length === 0) {
    return null;
  }

  // Helper function to format numbers by removing trailing zeros
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '?';
    // Convert to number if string
    const n = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(n)) return '?';
    // Round to 2 decimal places to avoid floating point precision issues
    const rounded = Math.round(n * 100) / 100;
    // Convert to string
    const str = rounded.toString();
    // Only remove trailing zeros if there's a decimal point
    if (str.includes('.')) {
      return str.replace(/\.?0+$/, '');
    }
    return str;
  };

  // Format Y-axis tick values
  const formatYAxis = (value) => {
    return formatNumber(value);
  };

  // Use last result for reference range
  const lastResult = timeSeries[timeSeries.length - 1];

  // Format date as MMM-YY for display (e.g., Jan-24)
  const formatDateDisplay = (dateString) => {
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${month}-${year}`;
  };

  // Format data for Recharts
  // Filter out negative magic numbers (-1 = spoiled, -2 = other issue, etc.)
  const chartData = timeSeries
    .filter(point => Number(point.value) >= 0)
    .map(point => ({
      date: new Date(point.date).getTime(), // Use timestamp for proper time series
      dateDisplay: formatDateDisplay(point.date), // For tooltip display
      value: Number(point.value), // Ensure value is a number
      unit: point.unit,
      isAbnormal: point.isAbnormal,
      normalMin: point.normalRangeMin,
      normalMax: point.normalRangeMax
    }));

  // Calculate Y-axis domain to ensure reference lines are visible
  // Convert all values to numbers to avoid string comparison issues
  // Filter out negative values (magic numbers like -1, -2 indicate spoiled samples, not real measurements)
  const allValues = timeSeries
    .map(p => Number(p.value))
    .filter(v => v >= 0 && !isNaN(v));

  // If no valid values, return null to avoid rendering
  if (allValues.length === 0) {
    return null;
  }

  const dataMin = Math.min(...allValues);
  const dataMax = Math.max(...allValues);
  const refMin = lastResult.normalRangeMin ? Number(lastResult.normalRangeMin) : null;
  const refMax = lastResult.normalRangeMax ? Number(lastResult.normalRangeMax) : null;

  let yMin = dataMin;
  let yMax = dataMax;

  if (refMin !== null && refMin !== undefined) {
    yMin = Math.min(yMin, refMin);
  }
  if (refMax !== null && refMax !== undefined) {
    yMax = Math.max(yMax, refMax);
  }

  // Add 5% padding above and below (reduced from 10% for tighter fit)
  const dataRange = yMax - yMin;
  const padding = dataRange * 0.05 || 1; // Use 1 if range is 0
  const rawMin = yMin - padding;
  const rawMax = yMax + padding;

  // Use a simpler approach: determine tick interval first, then round to it
  const range = rawMax - rawMin;

  // Determine nice tick interval based on range
  let tickInterval;
  if (range <= 2) tickInterval = 0.2;
  else if (range <= 5) tickInterval = 0.5;
  else if (range <= 10) tickInterval = 1;
  else if (range <= 20) tickInterval = 2;
  else if (range <= 50) tickInterval = 5;
  else if (range <= 100) tickInterval = 10;
  else if (range <= 200) tickInterval = 20;
  else if (range <= 500) tickInterval = 50;
  else tickInterval = 100;

  // Round min down and max up to nearest tick interval
  let niceMin = Math.floor(rawMin / tickInterval) * tickInterval;
  let niceMax = Math.ceil(rawMax / tickInterval) * tickInterval;

  // Ensure Y-axis never goes below zero
  niceMin = Math.max(0, niceMin);

  // Ensure domain values are numbers
  const yAxisDomain = [Number(niceMin), Number(niceMax)];

  // Generate ticks at the same interval used for domain calculation
  const generateTicks = () => {
    const [min, max] = yAxisDomain;

    // Generate ticks from min to max at the tick interval
    // Use integer multiplication to avoid floating point errors
    const ticks = [];
    let i = 0;
    while (true) {
      const tick = min + (i * tickInterval);
      if (tick > max + 0.0001) break; // Small epsilon for float comparison
      ticks.push(Number(tick));
      i++;
    }

    return ticks;
  };

  const yAxisTicks = generateTicks();

  // Generate X-axis ticks at nice date intervals
  const generateDateTicks = () => {
    if (chartData.length === 0) return [];

    const timestamps = chartData.map(d => d.date);
    const minTime = Math.min(...timestamps);
    const maxTime = Math.max(...timestamps);
    const timeRange = maxTime - minTime;

    // If all data points are on same date or very close, just return the unique dates
    if (timeRange < 1000 * 60 * 60 * 24 * 30) { // Less than 30 days
      return [...new Set(timestamps)].sort((a, b) => a - b);
    }

    // Determine appropriate interval based on time range
    const oneDay = 1000 * 60 * 60 * 24;
    const oneMonth = oneDay * 30;
    const oneYear = oneDay * 365;

    let interval;
    if (timeRange < oneYear) {
      // Less than 1 year: monthly ticks
      interval = oneMonth;
    } else if (timeRange < oneYear * 3) {
      // 1-3 years: 3-month ticks
      interval = oneMonth * 3;
    } else if (timeRange < oneYear * 5) {
      // 3-5 years: 6-month ticks
      interval = oneMonth * 6;
    } else {
      // 5+ years: yearly ticks
      interval = oneYear;
    }

    // Generate ticks
    const ticks = [];
    let currentTick = minTime;
    while (currentTick <= maxTime) {
      ticks.push(currentTick);
      currentTick += interval;
    }

    // Always include the last data point if not already included
    if (ticks[ticks.length - 1] < maxTime) {
      ticks.push(maxTime);
    }

    return ticks;
  };

  const xAxisTicks = generateDateTicks();

  return (
    <Card fluid>
      <Card.Content>
        <Card.Header>{testName}</Card.Header>
        <Card.Meta>{definition?.short_description || ''}</Card.Meta>
      </Card.Content>
      <Card.Content>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              type="number"
              domain={['dataMin', 'dataMax']}
              ticks={xAxisTicks}
              tickFormatter={(timestamp) => {
                const date = new Date(timestamp);
                const month = date.toLocaleDateString('en-US', { month: 'short' });
                const year = date.getFullYear().toString().slice(-2);
                return `${month}-${year}`;
              }}
              scale="time"
            />
            <YAxis
              domain={yAxisDomain}
              ticks={yAxisTicks}
              tickFormatter={formatYAxis}
            />
            <Tooltip
              labelFormatter={(timestamp) => {
                const date = new Date(timestamp);
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
              }}
            />

            {lastResult?.normalRangeMin && (
              <ReferenceLine
                y={lastResult.normalRangeMin}
                stroke="green"
                strokeDasharray="3 3"
                label="Min"
              />
            )}

            {lastResult?.normalRangeMax && (
              <ReferenceLine
                y={lastResult.normalRangeMax}
                stroke="green"
                strokeDasharray="3 3"
                label="Max"
              />
            )}

            <Line
              type="monotone"
              dataKey="value"
              stroke="#8884d8"
              strokeWidth={2}
              dot={{ fill: '#8884d8' }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Card.Content>
      <Card.Content extra>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: '#2185d0', fontSize: '1.05em', fontWeight: '500' }}>
            <strong>Latest:</strong> {formatNumber(lastResult.value)} {lastResult.unit}
            {lastResult.isAbnormal && <span style={{ color: 'red', marginLeft: '8px' }}>⚠️ Abnormal</span>}
          </div>
          {(lastResult.normalRangeMin || lastResult.normalRangeMax) && (
            <div style={{ color: '#666', fontSize: '0.9em' }}>
              <strong>Range:</strong> {formatNumber(lastResult.normalRangeMin)} - {formatNumber(lastResult.normalRangeMax)} {lastResult.unit}
            </div>
          )}
        </div>
      </Card.Content>
    </Card>
  );
}
